/**
 * LTD Page — Pórtico Lifetime Deal
 * 14 dias de janela. Contador público. Não estende, não reabre.
 * Design: editorial arquitetônico brasileiro (mesma linguagem da landing).
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';

type LtdTier = 'starter' | 'pro' | 'agencia';
interface LtdStock {
  tier: LtdTier;
  price_brl: number;
  seats_total: number;
  seats_left: number;
  closes_at: string;
}

// ─── Links Asaas (pagamento ÚNICO, não recorrente) ──────────────────────────
const LTD_LINKS = {
  starter: 'https://www.asaas.com/c/vuo52dds03pip8li',
  pro: 'https://www.asaas.com/c/t05qm0g203ls6zfz',
  agencia: 'https://www.asaas.com/c/2if05nufj569uq82',
};

const WA_NUMBER = '5511940373492';
const WA_LINK = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent('Oi, quero saber mais sobre o LTD do Pórtico.')}`;
const SIGNUP_LINK = '/auth/login';
const IG_HANDLE = '@portico.app';

// LTD expira em 14 dias a partir de 21/abr/2026 00:00 BRT
const LTD_DEADLINE = new Date('2026-05-05T03:00:00Z'); // 05/mai 00:00 BRT = 03:00 UTC

// ─── Demos ──────────────────────────────────────────────────────────────────
const DEMO = {
  before: '/demo/demo1-before.jpg',
  after: '/demo/demo1-after.png',
};

// ─── Countdown hook ─────────────────────────────────────────────────────────
function useCountdown(deadline: Date) {
  const calc = useCallback(() => {
    const diff = Math.max(0, deadline.getTime() - Date.now());
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return { d, h, m, s, expired: diff === 0 };
  }, [deadline]);

  const [time, setTime] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  }, [calc]);
  return time;
}

// ─── Before/After slider ────────────────────────────────────────────────────
function BeforeAfter({ before, after }: { before: string; after: string }) {
  const [pos, setPos] = useState(50);
  const ref = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const update = useCallback((clientX: number) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos(Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100)));
  }, []);

  useEffect(() => {
    const up = () => { dragging.current = false; };
    const move = (e: MouseEvent) => { if (dragging.current) update(e.clientX); };
    const touch = (e: TouchEvent) => { if (dragging.current) update(e.touches[0].clientX); };
    window.addEventListener('mouseup', up);
    window.addEventListener('mousemove', move);
    window.addEventListener('touchend', up);
    window.addEventListener('touchmove', touch, { passive: true });
    return () => {
      window.removeEventListener('mouseup', up);
      window.removeEventListener('mousemove', move);
      window.removeEventListener('touchend', up);
      window.removeEventListener('touchmove', touch);
    };
  }, [update]);

  return (
    <div
      ref={ref}
      className="relative w-full aspect-video cursor-ew-resize select-none overflow-hidden rounded-sm"
      onMouseDown={(e) => { dragging.current = true; update(e.clientX); }}
      onTouchStart={(e) => { dragging.current = true; update(e.touches[0].clientX); }}
    >
      <img src={after} alt="Depois" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img src={before} alt="Antes" className="absolute inset-0 w-full h-full object-cover" style={{ minWidth: `${10000 / Math.max(pos, 1)}%` }} />
      </div>
      <div className="absolute top-0 bottom-0" style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}>
        <div className="w-0.5 h-full bg-white/80" />
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-white/90 border-2 border-white flex items-center justify-center shadow-lg">
          <span className="text-xs text-neutral-900 font-mono">↔</span>
        </div>
      </div>
      <div className="absolute top-3 left-3 text-[10px] font-mono tracking-widest text-white/60 bg-black/40 px-2 py-0.5 rounded-sm">ANTES</div>
      <div className="absolute top-3 right-3 text-[10px] font-mono tracking-widest text-white/60 bg-black/40 px-2 py-0.5 rounded-sm">DEPOIS</div>
    </div>
  );
}

// ─── Countdown digit ────────────────────────────────────────────────────────
function Digit({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <span className="font-mono text-4xl md:text-6xl font-bold tabular-nums" style={{ color: 'hsl(28 42% 42%)' }}>
        {String(value).padStart(2, '0')}
      </span>
      <span className="text-[10px] font-mono tracking-widest text-neutral-400 mt-1">{label}</span>
    </div>
  );
}

// ─── Plan card ──────────────────────────────────────────────────────────────
function PlanCard({
  name, price, original, renders, features, link, popular, stock,
}: {
  name: string; price: string; original: string; renders: string;
  features: string[]; link: string; popular?: boolean;
  stock?: { seats_left: number; seats_total: number };
}) {
  const soldOut = stock?.seats_left === 0;
  return (
    <div className={`relative flex flex-col p-6 md:p-8 rounded-sm border ${popular ? 'border-[hsl(28_42%_42%)] bg-[hsl(24_18%_14%)]' : 'border-neutral-800 bg-[hsl(24_18%_10%)]'}`}>
      {popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-mono tracking-widest px-3 py-1 rounded-full" style={{ background: 'hsl(28 42% 42%)', color: 'hsl(36 22% 95%)' }}>
          MAIS POPULAR
        </div>
      )}
      <h3 className="font-serif text-xl md:text-2xl" style={{ color: 'hsl(36 22% 95%)' }}>{name}</h3>
      <p className="font-mono text-sm text-neutral-500 mt-1 line-through">{original}/mês</p>
      <p className="mt-2">
        <span className="font-serif text-4xl md:text-5xl font-bold" style={{ color: 'hsl(28 42% 42%)' }}>R$ {price}</span>
        <span className="text-sm text-neutral-400 ml-2 font-mono">pagamento único</span>
      </p>
      <p className="text-sm text-neutral-400 mt-1 font-mono">{renders} renders/mês · pra sempre</p>
      {stock && (
        <p className="text-[11px] mt-2 font-mono tracking-wider" style={{ color: soldOut ? 'hsl(0 60% 55%)' : 'hsl(28 42% 55%)' }}>
          {soldOut
            ? 'ESGOTADO'
            : `${stock.seats_left} / ${stock.seats_total} vagas restantes`}
        </p>
      )}
      <ul className="mt-6 space-y-2 flex-1">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm" style={{ color: 'hsl(36 22% 85%)' }}>
            <span style={{ color: 'hsl(28 42% 42%)' }}>✓</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {soldOut ? (
        <button disabled className="mt-6 block w-full text-center py-3 px-6 rounded-sm font-mono text-sm tracking-wide cursor-not-allowed" style={{ background: 'hsl(24 18% 14%)', color: 'hsl(0 0% 50%)', border: '1px solid hsl(24 18% 20%)' }}>
          ESGOTADO
        </button>
      ) : (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 block text-center py-3 px-6 rounded-sm font-mono text-sm tracking-wide transition-all"
          style={{
            background: popular ? 'hsl(28 42% 42%)' : 'transparent',
            color: popular ? 'hsl(36 22% 95%)' : 'hsl(28 42% 42%)',
            border: popular ? 'none' : '1px solid hsl(28 42% 42%)',
          }}
        >
          GARANTIR ACESSO VITALÍCIO
        </a>
      )}
    </div>
  );
}

// ─── FAQ item ───────────────────────────────────────────────────────────────
function Faq({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-neutral-800 py-4">
      <button onClick={() => setOpen(!open)} className="w-full flex justify-between items-center text-left">
        <span className="font-serif text-base md:text-lg" style={{ color: 'hsl(36 22% 90%)' }}>{q}</span>
        <span className="text-neutral-500 font-mono text-xl ml-4">{open ? '−' : '+'}</span>
      </button>
      {open && <p className="mt-3 text-sm leading-relaxed text-neutral-400">{a}</p>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// LTD PAGE
// ═══════════════════════════════════════════════════════════════════════════
export function LtdPage() {
  const { d, h, m, s, expired } = useCountdown(LTD_DEADLINE);
  const [stock, setStock] = useState<Record<LtdTier, LtdStock | undefined>>({
    starter: undefined, pro: undefined, agencia: undefined,
  });

  useEffect(() => {
    const fetchStock = async () => {
      const { data } = await supabase.rpc('ltd_inventory_public');
      const rows = (data as LtdStock[] | null) ?? [];
      setStock({
        starter: rows.find((r) => r.tier === 'starter'),
        pro: rows.find((r) => r.tier === 'pro'),
        agencia: rows.find((r) => r.tier === 'agencia'),
      });
    };
    void fetchStock();
    const id = setInterval(fetchStock, 30_000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: 'hsl(24 18% 8%)', color: 'hsl(36 22% 90%)' }}>

      {/* ── Topbar ─────────────────────────────────────────────────────── */}
      <header className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 md:px-8 py-3" style={{ background: 'hsla(24 18% 8% / 0.9)', backdropFilter: 'blur(12px)', borderBottom: '1px solid hsl(24 18% 16%)' }}>
        <a href="/" className="font-serif text-lg tracking-tight" style={{ color: 'hsl(36 22% 95%)' }}>pórtico</a>
        <div className="flex items-center gap-4">
          <span className="hidden md:inline text-xs font-mono text-neutral-500">Oferta encerra em {d}d {h}h {m}m</span>
          <a href="#planos" className="text-xs font-mono tracking-widest px-4 py-2 rounded-sm" style={{ background: 'hsl(28 42% 42%)', color: 'hsl(36 22% 95%)' }}>
            VER PLANOS LTD
          </a>
        </div>
      </header>

      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <section className="pt-24 md:pt-32 pb-16 px-4 md:px-8 max-w-5xl mx-auto text-center">
        <p className="font-mono text-xs tracking-[0.3em] text-neutral-500 mb-4">OFERTA DE LANÇAMENTO · LIFETIME DEAL</p>
        <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.1] mb-6" style={{ color: 'hsl(36 22% 95%)' }}>
          Pague uma vez.<br />
          <span style={{ color: 'hsl(28 42% 42%)' }}>Use pra sempre.</span>
        </h1>
        <p className="text-lg md:text-xl text-neutral-400 max-w-2xl mx-auto leading-relaxed">
          Render fotorrealista em 30 segundos. Estética brasileira. PIX. NFS-e automática.
          Acesso vitalício por menos que o custo de <em>um único render</em> terceirizado.
        </p>
      </section>

      {/* ── Countdown ──────────────────────────────────────────────────── */}
      <section className="pb-16 px-4">
        <div className="max-w-xl mx-auto flex justify-center gap-6 md:gap-10">
          <Digit value={d} label="DIAS" />
          <span className="font-mono text-4xl md:text-6xl text-neutral-700 self-start">:</span>
          <Digit value={h} label="HORAS" />
          <span className="font-mono text-4xl md:text-6xl text-neutral-700 self-start">:</span>
          <Digit value={m} label="MIN" />
          <span className="font-mono text-4xl md:text-6xl text-neutral-700 self-start">:</span>
          <Digit value={s} label="SEG" />
        </div>
        {expired && (
          <p className="text-center mt-6 font-mono text-sm text-red-400">Oferta encerrada.</p>
        )}
      </section>

      {/* ── Âncora de valor ────────────────────────────────────────────── */}
      <section className="py-16 px-4 md:px-8" style={{ background: 'hsl(24 18% 6%)' }}>
        <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-8 text-center">
          <div>
            <p className="font-mono text-xs tracking-widest text-neutral-500 mb-2">FREELANCER 3D NO BRASIL</p>
            <p className="font-serif text-4xl font-bold text-red-400 line-through">R$ 400–800</p>
            <p className="text-sm text-neutral-500 mt-1">por imagem · 7–10 dias</p>
          </div>
          <div>
            <p className="font-mono text-xs tracking-widest text-neutral-500 mb-2">PÓRTICO PRO</p>
            <p className="font-serif text-4xl font-bold" style={{ color: 'hsl(28 42% 42%)' }}>R$ 1,58</p>
            <p className="text-sm text-neutral-500 mt-1">por imagem · 30 segundos</p>
          </div>
          <div>
            <p className="font-mono text-xs tracking-widest text-neutral-500 mb-2">ECONOMIA</p>
            <p className="font-serif text-4xl font-bold" style={{ color: 'hsl(142 70% 45%)' }}>99,6%</p>
            <p className="text-sm text-neutral-500 mt-1">mais barato · 20.000x mais rápido</p>
          </div>
        </div>
      </section>

      {/* ── Before / After ─────────────────────────────────────────────── */}
      <section className="py-16 px-4 md:px-8 max-w-4xl mx-auto">
        <p className="font-mono text-xs tracking-widest text-neutral-500 mb-6 text-center">ARRASTE PARA COMPARAR</p>
        <BeforeAfter before={DEMO.before} after={DEMO.after} />
        <p className="text-center text-sm text-neutral-500 mt-4 font-mono">35 segundos · 2 784 × 1 504 px · estética brasileira nativa</p>
      </section>

      {/* ── Planos LTD ─────────────────────────────────────────────────── */}
      <section id="planos" className="py-20 px-4 md:px-8" style={{ background: 'hsl(24 18% 6%)' }}>
        <div className="max-w-5xl mx-auto">
          <p className="font-mono text-xs tracking-widest text-neutral-500 mb-2 text-center">LIFETIME DEAL · 14 DIAS</p>
          <h2 className="font-serif text-3xl md:text-5xl text-center mb-12" style={{ color: 'hsl(36 22% 95%)' }}>
            Escolha seu plano. <span style={{ color: 'hsl(28 42% 42%)' }}>Pague uma vez.</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <PlanCard
              name="LTD Starter"
              price="297"
              original="R$ 49"
              renders="20"
              features={[
                'Acesso vitalício',
                'Renders 16:9',
                '3 créditos grátis no cadastro',
                'Suporte via WhatsApp',
                'NFS-e automática',
                'PIX + Cartão',
              ]}
              link={LTD_LINKS.starter}
              stock={stock.starter}
            />
            <PlanCard
              name="LTD Pro"
              price="497"
              original="R$ 149"
              renders="80"
              features={[
                'Tudo do Starter +',
                'Cinematic 21:9',
                '3 variantes por render',
                'Fila prioritária',
                'Resolução 2784×1504',
                'Estética BR nativa (editorial)',
                'Garantia 7 dias',
              ]}
              link={LTD_LINKS.pro}
              popular
              stock={stock.pro}
            />
            <PlanCard
              name="LTD Agência"
              price="997"
              original="R$ 499"
              renders="350"
              features={[
                'Tudo do Pro +',
                'Multi-conta (até 5 users)',
                'API REST',
                'Branding customizado',
                'Gerente dedicado',
                'Onboarding personalizado',
                'Prioridade absoluta',
              ]}
              link={LTD_LINKS.agencia}
              stock={stock.agencia}
            />
          </div>
        </div>
      </section>

      {/* ── Social proof ───────────────────────────────────────────────── */}
      <section className="py-16 px-4 md:px-8 max-w-4xl mx-auto text-center">
        <p className="font-mono text-xs tracking-widest text-neutral-500 mb-4">ESTÉTICA BRASILEIRA NATIVA</p>
        <p className="font-serif text-xl md:text-2xl leading-relaxed" style={{ color: 'hsl(36 22% 80%)' }}>
          Travertino · Carvalho oak · Luz volumétrica de tarde · Paisagismo tropical
        </p>
        <p className="text-sm text-neutral-500 mt-2">Tom editorial brasileiro. Sem cara de render genérico internacional.</p>
      </section>

      {/* ── Garantia ───────────────────────────────────────────────────── */}
      <section className="py-16 px-4 md:px-8 text-center" style={{ background: 'hsl(24 18% 6%)' }}>
        <p className="font-mono text-xs tracking-widest text-neutral-500 mb-4">GARANTIA INCONDICIONAL</p>
        <h2 className="font-serif text-3xl md:text-4xl mb-4" style={{ color: 'hsl(36 22% 95%)' }}>
          7 dias. Não curtiu, devolvemos <span style={{ color: 'hsl(28 42% 42%)' }}>100%</span>.
        </h2>
        <p className="text-neutral-400 max-w-xl mx-auto">
          Testa o Pórtico por 7 dias. Se não fizer sentido pro teu escritório, devolvemos cada centavo via PIX em até 48h. Sem formulário, sem explicação.
        </p>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────── */}
      <section className="py-16 px-4 md:px-8 max-w-3xl mx-auto">
        <p className="font-mono text-xs tracking-widest text-neutral-500 mb-8 text-center">PERGUNTAS FREQUENTES</p>
        <Faq q="O que é Lifetime Deal?" a="Você paga uma vez e tem acesso ao plano escolhido pra sempre. Sem mensalidade, sem renovação. Os renders do mês renovam automaticamente todo mês." />
        <Faq q="Quanto tempo dura a oferta?" a="14 dias a partir de 21 de abril de 2026. O contador no topo da página é real. Quando zerar, os planos LTD saem do ar e ficam só os planos mensais." />
        <Faq q="Aceita PIX?" a="Sim. PIX, boleto bancário e cartão de crédito (até 12x sem juros). Processado pela Asaas." />
        <Faq q="Emite nota fiscal?" a="Sim, NFS-e automática após confirmação do pagamento. Sem pedir, sem esperar." />
        <Faq q="E se eu não gostar?" a="Garantia de 7 dias. Devolução 100% via PIX em até 48h, sem justificativa." />
        <Faq q="Posso cancelar depois?" a="O LTD é pagamento único — não tem assinatura pra cancelar. O acesso é seu pra sempre. A garantia de 7 dias é o único mecanismo de devolução." />
        <Faq q="Quantos renders por mês?" a="Starter: 20/mês. Pro: 80/mês. Agência: 350/mês. Renovam todo dia 1. Não acumula." />
        <Faq q="Funciona com SketchUp?" a="Sim. Envia JPG/PNG do seu modelo (export do SketchUp, Revit, ou até foto de mão livre) + descrição do ambiente. Entrega em 30 segundos." />
        <Faq q="A IA substitui meu render manual?" a="Pra 80% dos casos (apresentação de projeto, staging, portfolio Instagram), sim. Pra concurso internacional ou publicação editorial premium, você pode querer um retoque humano no topo — mas o Pórtico te dá a base em 30s em vez de 7 dias." />
      </section>

      {/* ── CTA final ──────────────────────────────────────────────────── */}
      <section className="py-20 px-4 md:px-8 text-center" style={{ background: 'hsl(24 18% 6%)' }}>
        <h2 className="font-serif text-3xl md:text-5xl mb-4" style={{ color: 'hsl(36 22% 95%)' }}>
          Do modelo ao retrato.<br />
          <span style={{ color: 'hsl(28 42% 42%)' }}>Em trinta segundos.</span>
        </h2>
        <p className="text-neutral-400 mb-8 max-w-lg mx-auto">
          Acesso vitalício. PIX. NFS-e. Garantia 7 dias. Oferta encerra em {d} dias.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="#planos"
            className="px-8 py-4 rounded-sm font-mono text-sm tracking-widest"
            style={{ background: 'hsl(28 42% 42%)', color: 'hsl(36 22% 95%)' }}
          >
            VER PLANOS LTD
          </a>
          <a
            href={SIGNUP_LINK}
            className="px-8 py-4 rounded-sm font-mono text-sm tracking-widest border"
            style={{ borderColor: 'hsl(28 42% 42%)', color: 'hsl(28 42% 42%)' }}
          >
            CRIAR CONTA GRÁTIS · 3 CRÉDITOS
          </a>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <footer className="py-8 px-4 md:px-8 text-center border-t" style={{ borderColor: 'hsl(24 18% 16%)' }}>
        <p className="text-xs text-neutral-600 font-mono">
          Pórtico · render.prospectar.site · {IG_HANDLE} · <a href={WA_LINK} className="underline">WhatsApp</a> · CNPJ em constituição
        </p>
      </footer>
    </div>
  );
}
